import { supabase } from "@/integrations/supabase/client";

export type ReadinessState = "ready" | "not_yet_required" | "pending" | "action_needed" | "missing";

export interface TaxReadinessRow {
  instructor_id: string;
  full_name: string | null;
  preferred_name: string | null;
  email: string | null;
  w9_request_id: string | null;
  collection_method: "online" | "pdf_upload" | "external_provider" | null;
  verification_status: "pending_review" | "verified" | "rejected" | "not_submitted" | null;
  tin_last4: string | null;
  tin_type: "ssn" | "ein" | null;
  completed_at: string | null;
  legal_name: string | null;
  w9_requested_at: string | null;
  w9_verified_at: string | null;
  ytd_earnings_cents: number;
  ytd_payment_entries: number;
  first_payment_at: string | null;
  last_payment_at: string | null;
  threshold_met: boolean;
  readiness_state: ReadinessState;
}

export interface TaxTimelineEvent {
  event_at: string;
  event_type: string;
  label: string;
  detail: string | null;
}

export interface ThresholdCrosser {
  instructor_id: string;
  full_name: string | null;
  preferred_name: string | null;
  crossed_at: string;
  running_total_cents: number;
}

export interface YearEndDashboard {
  ok: true;
  tax_year: number;
  counts: {
    total: number;
    ready_for_1099: number;
    not_yet_required: number;
    pending_review: number;
    action_needed: number;
    missing_w9: number;
    w9_complete: number;
  };
  drafts: { drafts: number; approved: number; issued: number };
  threshold_crossers_7d: ThresholdCrosser[];
  alerts: Array<{ level: "warn" | "info"; message: string; action?: string; href?: string }>;
}

export function maskTin(last4: string | null | undefined, type?: string | null): string {
  if (!last4) return "—";
  if (type === "ein") return `XX-XXX${last4}`;
  return `XXX-XX-${last4}`;
}

export function readinessLabel(state: ReadinessState): { icon: string; text: string; tone: "green" | "amber" | "orange" | "red" | "muted" } {
  switch (state) {
    case "ready": return { icon: "🟢", text: "Ready for 1099", tone: "green" };
    case "not_yet_required": return { icon: "🟡", text: "Not yet required", tone: "amber" };
    case "pending": return { icon: "🟠", text: "Pending review", tone: "orange" };
    case "action_needed": return { icon: "🔴", text: "Action required", tone: "red" };
    case "missing": return { icon: "🔴", text: "Missing W-9", tone: "red" };
  }
}

export async function fetchTaxReadiness(): Promise<TaxReadinessRow[]> {
  const { data, error } = await supabase.from("instructor_tax_readiness").select("*").order("full_name");
  if (error) throw error;
  return (data ?? []) as unknown as TaxReadinessRow[];
}

export async function callTaxCenter(body: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke("tax-center", { body });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

export async function callAdminW9Access(body: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke("admin-w9-access", { body });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

export const fetchYearEndDashboard = (tax_year?: number) =>
  callTaxCenter({ action: "year_end_dashboard", tax_year }) as Promise<YearEndDashboard>;

export const fetchTaxTimeline = (instructor_id: string, tax_year?: number) =>
  callTaxCenter({ action: "timeline", instructor_id, tax_year }) as Promise<{ ok: true; events: TaxTimelineEvent[] }>;

export const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;
